import { Reducer } from 'redux';

import {
    KnownAction,
    KnownTenants,
    TenantActions,
    TenantBasicSettings
} from './Actions';

import {
    BannerMessage,
    TenantUserMetadataSetting,
    PortalSafeDataCollectionLimitResponse,
    ConsumerTenantFeatureSwitches,
} from '../../Models/Api/strongbox.financialportal';

export type BrandThemeSettings = {
    palette: {
        modalBackground?: string;
        modalTitle?: string;
        modalText?: string;
        controlBackground?: string;
        controlForeground?: string;
        primaryColor?: string;
        secondaryColor?: string;
    },
    font: {
        modalFamily?: string;
        modalTitleSize?: string;
        modalTitleWeight?: string;
        modalRegularSize?: string;
        modalRegularWeight?: string;
        modalSecuritySize?: string;
        modalSecurityWeight?: string;
    }
};

export type BrandConfig = {
    themeSettings: BrandThemeSettings;
    cssSettings?: any;
};

export const normalButtonColor = '01bbc8';
export const normalButtonText = 'white';

export const defaultBrandConfig: BrandConfig = {
    themeSettings: {
        palette: {
            controlBackground: normalButtonColor,
            controlForeground: normalButtonText,
        },
        font: {}
    }
}

export interface ITenantState {
    tenantName: string;
    tenantNameId: string;
    basicSettings: TenantBasicSettings | undefined;
    brandConfig: BrandConfig;
    emailDomains: string[];  // the list of email domains that can be used for a member of the tenant space, e.g.
    // valid domains that can be used inviting a user.
    userMetadataSettings: TenantUserMetadataSetting[]; // The possible set of additional metadata that can be set for a user
    bannerMessages: BannerMessage[]; // Messages that show up at the top of the portal based on tenant, feature availability, etc.
    dataCollectionLimits: PortalSafeDataCollectionLimitResponse | undefined; // If this is undefined the tenant should be assumed to not have limits
    featureSwitches: ConsumerTenantFeatureSwitches | undefined; // The feature switch values configured for the tenant. Undefined until loaded.
}

const defaultState: ITenantState = {
    tenantName: KnownTenants.strongbox,
    tenantNameId: KnownTenants.strongbox,
    basicSettings: undefined,
    brandConfig: defaultBrandConfig,
    emailDomains: [],
    userMetadataSettings: [],
    bannerMessages: [],
    dataCollectionLimits: undefined,
    featureSwitches: undefined,
}

export const tenantOverrideKey = 'tenantNameOverride';

export const reducer: Reducer<ITenantState, KnownAction> = (state: ITenantState | undefined, action: KnownAction): ITenantState => {
    let newState: ITenantState | undefined = undefined;

    switch (action.type) {
        case TenantActions.TenantSetBasicSettings: {
            newState = {
                ...(state || defaultState),
                basicSettings: action.settings,
            };
            break;
        }

        case TenantActions.TenantOverrideActiveTenant: {
            newState = {
                ...(state || defaultState)
            };

            newState.tenantName = action.tenant;
            localStorage.setItem(tenantOverrideKey, newState.tenantName);

            break;
        }

        case TenantActions.TenantSetBrandConfig: {
            newState = {
                ...(state || defaultState),
                brandConfig: {
                    ...action.brandconfig
                }
            };
            break;
        }

        case TenantActions.TenantListEmailDomains: {
            // Nothing to do here now.
            break;
        }

        case TenantActions.TenantListEmailDomainsComplete: {
            newState = {
                ...(state || defaultState),
                emailDomains: action.domains.slice(),
            };
            break;
        }

        case TenantActions.TenantListUserMetadataSettings: {
            // Nothing to do here yet.
            break;
        }

        case TenantActions.TenantListUserMetadataSettingsComplete: {
            newState = {
                ...(state || defaultState),
                userMetadataSettings: action.metadataSettings.slice(),
            }
            break;
        }

        case TenantActions.TenantGetBannerMessages: {
            // Nothing to do here yet.
            break;
        }

        case TenantActions.TenantGetBannerMessagesComplete: {
            newState = {
                ...(state || defaultState),
                bannerMessages: action.messages.slice(),
            }
            break;
        }
        case TenantActions.TenantGetDataCollectionLimit: {
            // nothing to do here yet.
            break;
        }
        case TenantActions.TenantGetDataCollectionLimitComplete: {
            newState = {
                ...(state || defaultState),
                dataCollectionLimits: !!action.limits ? { ...action.limits } : undefined,
            }
            break;
        }
        case TenantActions.TenantGetBasicSettingsComplete: {
            if (!!action.settings) {
                newState = {
                    ...(state || defaultState),
                    tenantName: action.settings.name,
                    tenantNameId: action.settings.nameId,
                }
            }
            break;
        }
        case TenantActions.TenantGetFeatureSwitches: {
            // Nothing to do here yet.
            break;
        }
        case TenantActions.TenantGetFeatureSwitchesComplete: {
            newState = {
                ...(state || defaultState),
                featureSwitches: !!action.featureSwitches ? { ...action.featureSwitches } : undefined,
            }
            break;
        }
    }
    return newState ? newState : state ? state : defaultState;
}
